import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import IndependentConnectionDisclaimer from '../components/IndependentConnectionDisclaimer';
import NotAFitSettingsList from '../components/NotAFitSettingsList';
import { TextSizeControl } from '../components/accessibility/TextSizeControl';

export const SettingsPage: React.FC = () => {
  const { state, dispatch } = useApp();
  const navigate = useNavigate();

  const handleThemeToggle = () => {
    const newTheme = state.theme === 'light' ? 'dark' : 'light';
    dispatch({ type: 'SET_THEME', payload: newTheme });
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h4" gutterBottom>
                Settings
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage your app preferences and account settings
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Appearance
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Text Size"
                    secondary="Adjust text size across the app (small, default, or large)"
                  />
                  <ListItemSecondaryAction>
                    <TextSizeControl
                      value={state.textSize}
                      onChange={(size) => dispatch({ type: 'SET_TEXT_SIZE', payload: size })}
                      compact
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Dark Mode"
                    secondary="Switch between light and dark themes"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      edge="end"
                      checked={state.theme === 'dark'}
                      onChange={handleThemeToggle}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Profile"
                    secondary="Manage your profile information"
                  />
                  <ListItemSecondaryAction>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('/profile')}
                    >
                      View
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Data Access"
                    secondary="Request an export or deletion of your data (MFA required)"
                  />
                  <ListItemSecondaryAction>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('/settings/data-access')}
                      data-testid="data-access-settings-link"
                      sx={{ '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}
                    >
                      Manage
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Privacy"
                    secondary="Manage your privacy settings"
                  />
                  <ListItemSecondaryAction>
                    <Button
                      variant="outlined"
                      size="small"
                    >
                      Configure
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="not-a-fit-settings-card">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hidden Profiles
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                People you've marked &quot;Not a fit&quot;. They aren&apos;t notified, and
                you can restore visibility anytime.
              </Typography>
              <NotAFitSettingsList />
            </CardContent>
          </Card>
        </Grid>

        {/* FEAT-040 — Settings "About Careva" surfaces the
            Independent-Connection disclaimer at any time (MVP-18.40). */}
        <Grid item xs={12} data-testid="settings-about-careva">
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                About Careva
              </Typography>
              <IndependentConnectionDisclaimer testId="settings-disclaimer" />
              <Typography variant="body2" paragraph>
                Careva is a warm place to find caregivers and the people they look
                after. Browse, chat, and arrange care on your own terms.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="outlined" size="small">
                  View Documentation
                </Button>
                <Button variant="outlined" size="small">
                  Report Issue
                </Button>
                <Button variant="outlined" size="small">
                  Check for Updates
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
